import { AnalyticsEvent } from 'services/analytics'
import { CanonicalLoad, canonicalLoadboardAnalyticsName } from '@numeo/types-core'
import { getLoadUid } from './loadKey'
import { createLoadHubAnalyticsId, LoadHubAnalyticsProperties, trackLoadHubEvent } from './analyticsContext'
import { SearchCorrelationContext } from './searchAnalytics'

interface LoadAttribution extends SearchCorrelationContext {
    displayedRank?: number
}

const attributionByLoad = new Map<string, LoadAttribution>()
const MAX_LOAD_ATTRIBUTIONS = 5_000
let activeLoadHubSearchTabId: string | undefined

const attributionKey = (load: CanonicalLoad, searchTabId: string | undefined = activeLoadHubSearchTabId): string =>
    `${searchTabId ?? 'unscoped'}:${canonicalLoadboardAnalyticsName(load.sourceType || 'UNKNOWN')}:${getLoadUid(load)}`

export const setActiveLoadHubSearchTab = (searchTabId: string | undefined): void => {
    activeLoadHubSearchTabId = searchTabId
}

const rememberAttribution = (key: string, attribution: LoadAttribution): void => {
    // Load Hub can stay open all day with auto-refresh. Bound the module cache so
    // stale load IDs do not accumulate for the lifetime of the browser tab.
    attributionByLoad.delete(key)
    attributionByLoad.set(key, attribution)
    while (attributionByLoad.size > MAX_LOAD_ATTRIBUTIONS) {
        const oldest = attributionByLoad.keys().next().value
        if (oldest === undefined) break
        attributionByLoad.delete(oldest)
    }
}

export const registerLoadSearchAttribution = (loads: CanonicalLoad[], context: SearchCorrelationContext, searchTabId: string, rankOffset: number = 0): void => {
    loads.forEach((load, index) => rememberAttribution(attributionKey(load, searchTabId), { ...context, displayedRank: rankOffset + index + 1 }))
}

/** Called by the rendered, sorted table so action events use the rank the user actually saw. */
export const setDisplayedLoadRank = (load: CanonicalLoad, displayedRank: number): void => {
    const key = attributionKey(load)
    const current = attributionByLoad.get(key)
    if (current) rememberAttribution(key, { ...current, displayedRank: Math.max(1, displayedRank) })
}

const loadProperties = (load: CanonicalLoad): LoadHubAnalyticsProperties => {
    const attribution = attributionByLoad.get(attributionKey(load))
    const loadboard = canonicalLoadboardAnalyticsName(load.sourceType || 'UNKNOWN')
    return {
        load_id: `${loadboard}:${load.externalId || load.id}`,
        loadboard,
        attribution_status: attribution ? 'linked' : 'unattributed',
        ...(attribution
            ? {
                  search_id: attribution.searchId,
                  search_execution_id: attribution.searchExecutionId,
                  displayed_rank: attribution.displayedRank,
              }
            : {}),
        has_email: Boolean(load.brokerContactEmail || load.posterContactEmail),
        has_phone: Boolean(load.brokerContactPhone),
        has_telegram: Boolean(load.brokerTelegramAccountLink),
    }
}

export const trackLoadDetailsOpened = (load: CanonicalLoad, openLocation: 'table_row' | 'new_tab' | 'other' = 'table_row'): void => {
    trackLoadHubEvent(AnalyticsEvent.WEB_LOAD_HUB_LOAD_DETAILS_OPENED, {
        ...loadProperties(load),
        open_location: openLocation,
    })
}

export const trackLoadDetailsResolved = (params: {
    load: CanonicalLoad
    detailRequestId: string
    outcome: 'success' | 'failed' | 'cancelled' | 'timeout'
    latencyMs: number
    errorCode?: string
}): void => {
    trackLoadHubEvent(
        AnalyticsEvent.WEB_LOAD_HUB_LOAD_DETAILS_RESOLVED,
        {
            ...loadProperties(params.load),
            detail_request_id: params.detailRequestId,
            outcome: params.outcome,
            detail_latency_ms: Math.max(0, params.latencyMs),
            contact_source: 'load',
            ...(params.errorCode
                ? {
                      error_id: createLoadHubAnalyticsId('err'),
                      error_category: params.outcome === 'timeout' ? 'network' : 'provider',
                      error_code: params.errorCode,
                      failure_stage: 'detail_fetch',
                      is_retriable: true,
                      user_visible: false,
                  }
                : {}),
        },
        { humanActivity: false }
    )
}

export const trackDetailToolOpened = (load: CanonicalLoad, detailTool: 'trip' | 'map' | 'tolls' | 'profitability' | 'factoring' | 'email_thread'): void => {
    trackLoadHubEvent(AnalyticsEvent.WEB_LOAD_HUB_UI_ACTION, {
        ...loadProperties(load),
        action: 'detail_tool_opened',
        detail_tool: detailTool,
    })
}

export const trackLoadContactClicked = (
    load: CanonicalLoad,
    channel: 'email' | 'phone' | 'telegram',
    entryPoint: 'table_row' | 'load_details' | 'email_thread' | 'other',
    callProvider: 'call' | 'googlevoice' | 'ringcentral' | 'not_applicable' | 'unknown' = 'not_applicable'
): string => {
    const contactAttemptId = createLoadHubAnalyticsId('contact')
    trackLoadHubEvent(AnalyticsEvent.WEB_LOAD_HUB_CONTACT_CLICKED, {
        ...loadProperties(load),
        contact_attempt_id: contactAttemptId,
        channel,
        entry_point: entryPoint,
        call_provider: channel === 'phone' ? callProvider : 'not_applicable',
    })
    return contactAttemptId
}

export const trackLoadContactCopied = (
    load: CanonicalLoad,
    copiedField: 'phone' | 'email' | 'mc' | 'dot',
    entryPoint: 'table_row' | 'load_details' | 'email_thread' | 'other'
): void => {
    trackLoadHubEvent(AnalyticsEvent.WEB_LOAD_HUB_CONTACT_COPIED, {
        ...loadProperties(load),
        copied_field: copiedField,
        entry_point: entryPoint,
    })
}

export const trackCallHandoffResolved = (params: {
    load: CanonicalLoad
    contactAttemptId: string
    callProvider: 'call' | 'googlevoice' | 'ringcentral' | 'unknown'
    outcome: 'launched' | 'failed' | 'unobservable'
}): void => {
    trackLoadHubEvent(
        AnalyticsEvent.WEB_LOAD_HUB_CALL_HANDOFF_RESOLVED,
        {
            ...loadProperties(params.load),
            contact_attempt_id: params.contactAttemptId,
            call_provider: params.callProvider,
            outcome: params.outcome,
        },
        { humanActivity: false }
    )
}

type DetailedEmailType = 'inquiry' | 'offer' | 'booking_request' | 'info_request' | 'reply'

interface EmailSendAnalyticsContext {
    sendCommandId: string
    sendAttemptId: string
    composeId?: string
    emailType: DetailedEmailType
    properties: LoadHubAnalyticsProperties
}

export interface ServerEmailAnalyticsPayload {
    schemaVersion: 1
    sendCommandId: string
    sendAttemptId: string
    composeId?: string
    emailType: DetailedEmailType
    loadId: string
    loadboard: string
    searchId?: string
    searchExecutionId?: string
    startedAt: string
}

export const trackEmailComposerOpened = (params: {
    load: CanonicalLoad
    emailType: DetailedEmailType
    emailConnected: boolean
    entryPoint: 'table_row' | 'load_details' | 'email_thread' | 'other'
    senderAccountId?: string
}): string => {
    const composeId = createLoadHubAnalyticsId('compose')
    trackLoadHubEvent(AnalyticsEvent.WEB_LOAD_HUB_EMAIL_COMPOSER_OPENED, {
        ...loadProperties(params.load),
        compose_id: composeId,
        email_type: params.emailType,
        email_connected: params.emailConnected,
        entry_point: params.entryPoint,
        ...(params.senderAccountId ? { sender_account_id: params.senderAccountId } : {}),
    })
    return composeId
}

export const trackEmailSendSubmitted = (params: {
    load: CanonicalLoad
    emailType: DetailedEmailType
    recipientCount: number
    composeId?: string
    senderAccountId?: string
    templateId?: string
}): EmailSendAnalyticsContext => {
    const context: EmailSendAnalyticsContext = {
        sendCommandId: createLoadHubAnalyticsId('send_cmd'),
        sendAttemptId: createLoadHubAnalyticsId('send_attempt'),
        composeId: params.composeId,
        emailType: params.emailType,
        properties: loadProperties(params.load),
    }
    trackLoadHubEvent(AnalyticsEvent.WEB_LOAD_HUB_EMAIL_SEND_SUBMITTED, {
        ...context.properties,
        send_command_id: context.sendCommandId,
        send_attempt_id: context.sendAttemptId,
        ...(context.composeId ? { compose_id: context.composeId } : {}),
        email_type: context.emailType,
        recipient_count: Math.max(1, params.recipientCount),
        initiator: 'user',
        ...(params.senderAccountId ? { sender_account_id: params.senderAccountId } : {}),
        ...(params.templateId ? { template_id: params.templateId } : {}),
    })
    return context
}

export const trackEmailRequestFailed = (
    context: EmailSendAnalyticsContext,
    failureStage: 'validation' | 'transport' | 'response_handling',
    sendOutcome: 'not_sent' | 'unknown',
    errorCode: string
): void => {
    trackLoadHubEvent(
        AnalyticsEvent.WEB_LOAD_HUB_EMAIL_REQUEST_FAILED,
        {
            ...context.properties,
            send_command_id: context.sendCommandId,
            send_attempt_id: context.sendAttemptId,
            ...(context.composeId ? { compose_id: context.composeId } : {}),
            email_type: context.emailType,
            failure_stage: failureStage,
            send_outcome: sendOutcome,
            error_category: failureStage === 'validation' ? 'validation' : 'network',
            error_code: errorCode,
            error_id: createLoadHubAnalyticsId('err'),
            is_retriable: failureStage !== 'validation',
            user_visible: true,
        },
        { humanActivity: false }
    )
}

/** Validation never creates a send attempt: only the user's command and compose are recorded. */
export const trackEmailValidationFailed = (params: {
    load: CanonicalLoad
    emailType: DetailedEmailType
    composeId?: string
    errorCode: 'not_authenticated' | 'recipient_missing' | 'sender_missing' | 'content_missing' | 'invalid_offer_rate' | 'read_scope_missing'
}): void => {
    trackLoadHubEvent(AnalyticsEvent.WEB_LOAD_HUB_EMAIL_REQUEST_FAILED, {
        ...loadProperties(params.load),
        send_command_id: createLoadHubAnalyticsId('send_cmd'),
        ...(params.composeId ? { compose_id: params.composeId } : {}),
        email_type: params.emailType,
        failure_stage: 'validation',
        send_outcome: 'not_sent',
        error_category: 'validation',
        error_code: params.errorCode,
        error_id: createLoadHubAnalyticsId('err'),
        is_retriable: false,
        user_visible: true,
    })
}

export const toServerEmailAnalytics = (context: EmailSendAnalyticsContext): ServerEmailAnalyticsPayload => ({
    schemaVersion: 1,
    sendCommandId: context.sendCommandId,
    sendAttemptId: context.sendAttemptId,
    ...(context.composeId ? { composeId: context.composeId } : {}),
    emailType: context.emailType,
    loadId: String(context.properties.load_id),
    loadboard: String(context.properties.loadboard),
    ...(typeof context.properties.search_id === 'string' ? { searchId: context.properties.search_id } : {}),
    ...(typeof context.properties.search_execution_id === 'string' ? { searchExecutionId: context.properties.search_execution_id } : {}),
    startedAt: new Date().toISOString(),
})

export const toDetailedEmailType = (type: 'quick' | 'info' | 'offer' | 'book'): DetailedEmailType => {
    if (type === 'quick') return 'inquiry'
    if (type === 'info') return 'info_request'
    if (type === 'book') return 'booking_request'
    return 'offer'
}
